import { Group, MultiSelect, Radio, Stack, Text } from '@mantine/core';
import { IconUser, IconVideo, IconBuildingHospital, IconArrowsLeftRight } from '@tabler/icons-react';
import type { FormatPreference } from '../types';
import type { IntakeAnswers } from '../types';
import type { IntakeAction } from '../types';
import { AVAILABILITY_DAYS_OPTIONS, AVAILABILITY_TIME_OPTIONS } from '../bookingConstraints';
import { isFormatPreferenceIntakeDisabled } from '../intakeTemporaryRestrictions';

const OPTIONS: { value: FormatPreference; label: string; icon: React.ReactNode }[] = [
  { value: 'in-person', label: 'In person', icon: <IconUser size={18} /> },
  { value: 'virtual', label: 'Virtual (video or phone)', icon: <IconVideo size={18} /> },
  { value: 'hybrid', label: 'Hybrid (In-person & telehealth)', icon: <IconBuildingHospital size={18} /> },
  { value: 'no-preference', label: 'No preference', icon: <IconArrowsLeftRight size={18} /> },
];

const TIME_DATA = AVAILABILITY_TIME_OPTIONS.map((c) => ({ value: c.value, label: c.label }));
const DAYS_DATA = AVAILABILITY_DAYS_OPTIONS.map((c) => ({ value: c.value, label: c.label }));

interface StepFormatProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
}

export function StepFormat({ answers, dispatch }: StepFormatProps): JSX.Element {
  return (
    <Stack gap="lg">
      <Radio.Group
        value={answers.format ?? ''}
        onChange={(value) => dispatch({ type: 'SET_FORMAT', payload: value as FormatPreference })}
      >
        <Stack gap="sm">
          {OPTIONS.map((opt) => (
            <Radio
              key={opt.value}
              value={opt.value}
              disabled={isFormatPreferenceIntakeDisabled(opt.value)}
              label={
                <Group gap="xs">
                  <span style={{ color: 'var(--mantine-color-gray-6)' }}>{opt.icon}</span>
                  <Text span fw={500} size="sm">
                    {opt.label}
                  </Text>
                </Group>
              }
            />
          ))}
        </Stack>
      </Radio.Group>

      <Stack gap="sm">
        <Text size="sm" fw={500} c="dimmed">
          Availability (optional)
        </Text>
        <MultiSelect
          label="When are you not available? (time of day) Select all that apply."
          placeholder="Select..."
          data={TIME_DATA}
          value={answers.availabilityTimeOfDay}
          onChange={(value) => dispatch({ type: 'SET_AVAILABILITY_TIME_OF_DAY', payload: value })}
          clearable
        />
        <MultiSelect
          label="When are you not available? (days) Select all that apply."
          placeholder="Select..."
          data={DAYS_DATA}
          value={answers.availabilityDays}
          onChange={(value) => dispatch({ type: 'SET_AVAILABILITY_DAYS', payload: value })}
          clearable
        />
      </Stack>
    </Stack>
  );
}
